import { ThemeEvents } from '@theme/events';

const STOREFRONT_API_VERSION = '2025-01';
const STOREFRONT_GRAPHQL_ENDPOINT = `/api/${STOREFRONT_API_VERSION}/graphql.json`;

function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

const CART_CREATE_MUTATION = `
  mutation KioskCartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
      warnings {
        code
        message
      }
    }
  }
`;

class KioskCartCheckout {
  constructor() {
    this.checkoutCache = null;
    this.context = null;
    this.storefrontToken = null;
    this.hasValidToken = false;
    this.discountApplyInFlight = false;
    this.mutationObserver = null;
    this.syncRafId = null;
    this.syncTimeoutId = null;
    this.syncDelayedTimeoutId = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleDomMutations = this.handleDomMutations.bind(this);
  }

  init() {
    const isKioskEnabled = this.isKioskEnabled();
    if (!isKioskEnabled) {
      this.resetToRegularCartState();
      return;
    }

    this.context = this.getContext();
    this.storefrontToken =
      window.theme?.storefrontAccessToken ||
      window.configuratorData?.storefrontAccessToken ||
      null;
    this.hasValidToken = typeof this.storefrontToken === 'string' && this.storefrontToken.trim().length > 0;

    this.applyKioskState();
    this.maybeApplyEventDiscount();
    this.startDomObserver();

    document.addEventListener('click', this.handleClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  isKioskEnabled() {
    if (window.kioskContext && typeof window.kioskContext.isEnabled === 'function') {
      return Boolean(window.kioskContext.isEnabled());
    }

    const params = new URLSearchParams(window.location.search);
    return params.get('kiosk') === '1';
  }

  getContext() {
    if (window.kioskContext && typeof window.kioskContext.get === 'function') {
      return window.kioskContext.get();
    }

    const params = new URLSearchParams(window.location.search);
    return {
      kiosk: params.get('kiosk'),
      event_source: params.get('event_source'),
      event_code: params.get('event_code'),
    };
  }

  resetToRegularCartState() {
    const roots = this.getRoots();
    roots.forEach((root) => {
      root.hidden = true;
      delete root.dataset.loading;
      this.setScanButtonLabel(root, 'Scanner pour payer');

      const host = root.closest('.cart__ctas');
      if (host) {
        host.removeAttribute('data-kiosk-enabled');
        host.removeAttribute('data-storefront-token');
      }

      const checkoutButton = this.getCheckoutButton(root);
      if (checkoutButton) {
        if (checkoutButton.dataset.defaultLabel) {
          checkoutButton.textContent = checkoutButton.dataset.defaultLabel;
        }
        checkoutButton.classList.remove('button--secondary');
      }
    });
  }

  applyKioskState() {
    const roots = this.getRoots();

    roots.forEach((root) => {
      root.hidden = false;

      const host = root.closest('.cart__ctas');
      if (host) {
        host.setAttribute('data-kiosk-enabled', 'true');
        host.setAttribute('data-storefront-token', this.hasValidToken ? 'valid' : 'missing');
      }

      const checkoutButton = this.getCheckoutButton(root);
      if (checkoutButton) {
        if (!checkoutButton.dataset.defaultLabel) {
          checkoutButton.dataset.defaultLabel = checkoutButton.textContent?.trim() || 'Payer';
        }
        checkoutButton.textContent = 'Payer sur la tablette';
        checkoutButton.classList.add('button--secondary');
      }

      if (!this.hasValidToken) {
        this.setButtonsDisabled(root, true);
        this.setScanButtonLabel(root, 'Scanner indisponible');
      } else {
        this.setScanButtonLabel(root, 'Scanner pour payer');
      }
    });
  }

  getRoots() {
    return Array.from(document.querySelectorAll('[data-kiosk-cart-checkout]'));
  }

  getRootFromTarget(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    return target.closest('[data-kiosk-cart-checkout]');
  }

  setButtonsDisabled(root, isDisabled) {
    const buttons = root.querySelectorAll('button[data-kiosk-scan-checkout]');
    buttons.forEach((button) => {
      button.disabled = isDisabled;
    });
  }

  getCheckoutButton(root) {
    const host = root.closest('.cart__ctas');
    if (!host) {
      return null;
    }

    const checkoutButton = host.querySelector('button.cart__checkout-button');
    if (!(checkoutButton instanceof HTMLButtonElement)) {
      return null;
    }

    return checkoutButton;
  }

  setScanButtonLabel(root, label) {
    const button = root.querySelector('button[data-kiosk-scan-checkout]');
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    button.textContent = label;
  }

  setBusy(root, isBusy) {
    root.dataset.loading = isBusy ? 'true' : 'false';
    this.setButtonsDisabled(root, isBusy || !this.hasValidToken);
  }

  clearCheckoutCache() {
    this.checkoutCache = null;

    this.getRoots().forEach((root) => {
      const container = root.querySelector('[data-kiosk-qr-container]');

      if (container) {
        container.innerHTML = '';
      }
    });
  }

  handleCartUpdate() {
    this.clearCheckoutCache();
    this.scheduleStateSync();
  }

  startDomObserver() {
    if (this.mutationObserver) {
      return;
    }

    this.mutationObserver = new MutationObserver(this.handleDomMutations);
    this.mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  handleDomMutations(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        if (
          node.matches('[data-kiosk-cart-checkout]') ||
          node.querySelector('[data-kiosk-cart-checkout]') ||
          node.matches('.cart__ctas') ||
          node.querySelector('.cart__ctas')
        ) {
          this.scheduleStateSync();
          return;
        }
      }
    }
  }

  scheduleStateSync() {
    if (this.syncRafId) {
      cancelAnimationFrame(this.syncRafId);
    }

    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }

    if (this.syncDelayedTimeoutId) {
      clearTimeout(this.syncDelayedTimeoutId);
    }

    this.syncRafId = requestAnimationFrame(() => {
      this.syncTimeoutId = window.setTimeout(() => {
        this.applyKioskState();
        this.maybeApplyEventDiscount();
      }, 0);
    });

    this.syncDelayedTimeoutId = window.setTimeout(() => {
      this.applyKioskState();
      this.maybeApplyEventDiscount();
    }, 150);
  }

  async handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const closeButton = target.closest('[data-kiosk-qr-close]');
    if (closeButton) {
      event.preventDefault();
      const root = this.getRootFromTarget(target);
      this.closeDialog(root);
      return;
    }

    const dialog = target.closest('dialog[data-kiosk-qr-modal]');
    if (dialog && target === dialog) {
      dialog.close();
      return;
    }

    const scanButton = target.closest('button[data-kiosk-scan-checkout]');
    if (scanButton) {
      event.preventDefault();
      const root = this.getRootFromTarget(scanButton);
      if (!root) {
        return;
      }

      await this.handleScan(root);
      return;
    }

    const checkoutButton = target.closest('button.cart__checkout-button');
    if (checkoutButton) {
      const host = checkoutButton.closest('.cart__ctas');
      if (!host || host.getAttribute('data-kiosk-enabled') !== 'true') {
        return;
      }

      event.preventDefault();
      this.handleTabletCheckout();
    }
  }

  closeDialog(root) {
    if (!root) {
      return;
    }

    const dialog = root.querySelector('dialog[data-kiosk-qr-modal]');
    if (dialog && typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    }
  }

  openDialog(root) {
    if (!root) {
      return;
    }

    const dialog = root.querySelector('dialog[data-kiosk-qr-modal]');
    if (!(dialog instanceof HTMLDialogElement)) {
      return;
    }

    if (!dialog.open) {
      dialog.showModal();
    }
  }

  handleTabletCheckout() {
    const checkoutUrl = new URL('/checkout', window.location.origin);

    const eventCode = this.context?.event_code;
    if (!isBlank(eventCode)) {
      checkoutUrl.searchParams.set('discount', String(eventCode).trim());
    }

    window.location.assign(checkoutUrl.toString());
  }

  hasEventDiscountApplied(component, eventCode) {
    const normalizedCode = eventCode.toLowerCase();
    const pills = component.querySelectorAll('.cart-discount__pill[data-discount-code]');
    return Array.from(pills).some((pill) => {
      const code = pill.getAttribute('data-discount-code');
      return typeof code === 'string' && code.trim().toLowerCase() === normalizedCode;
    });
  }

  maybeApplyEventDiscount() {
    const eventCodeRaw = this.context?.event_code;
    if (isBlank(eventCodeRaw) || this.discountApplyInFlight) {
      return;
    }

    const eventCode = String(eventCodeRaw).trim();
    const discountComponents = Array.from(document.querySelectorAll('cart-discount-component'));
    if (discountComponents.length === 0) {
      return;
    }

    const discountComponent =
      discountComponents.find(
        (component) => component instanceof HTMLElement && component.getClientRects().length > 0
      ) || discountComponents[0];

    if (this.hasEventDiscountApplied(discountComponent, eventCode)) {
      return;
    }

    const form = discountComponent.querySelector('form.cart-discount__form');
    const input = discountComponent.querySelector('input[name="discount"]');
    if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) {
      return;
    }

    this.discountApplyInFlight = true;
    input.value = eventCode;
    form.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      })
    );

    window.setTimeout(() => {
      this.discountApplyInFlight = false;
    }, 1200);
  }

  async handleScan(root) {
    if (!this.hasValidToken) {
      this.setScanButtonLabel(root, 'Scanner indisponible');
      return;
    }

    this.setBusy(root, true);
    this.setScanButtonLabel(root, 'Génération du QR…');
    let didSucceed = false;

    try {
      const { checkoutUrl } = await this.getCheckoutUrl();
      this.renderQr(root, checkoutUrl);
      this.openDialog(root);
      didSucceed = true;
    } catch (error) {
      this.setScanButtonLabel(root, 'Réessayer le scan');
      window.setTimeout(() => {
        this.setScanButtonLabel(root, 'Scanner pour payer');
      }, 1800);
    } finally {
      this.setBusy(root, false);
      if (this.hasValidToken && didSucceed) {
        this.setScanButtonLabel(root, 'Scanner pour payer');
      }
    }
  }

  renderQr(root, checkoutUrl) {
    const container = root.querySelector('[data-kiosk-qr-container]');

    if (container) {
      container.innerHTML = '';

      const qrCode = document.createElement('qr-code-image');
      qrCode.setAttribute('width', '260');
      qrCode.setAttribute('height', '260');
      qrCode.setAttribute('data-identifier', checkoutUrl);
      qrCode.setAttribute('alt', 'QR code vers le checkout');
      qrCode.setAttribute('color-dark', '#000000');
      qrCode.setAttribute('color-light', '#0000');

      container.appendChild(qrCode);
    }
  }

  buildCartFingerprint(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const serializedItems = items
      .map((item) => {
        const serializedProperties = JSON.stringify(item.properties || {});
        return `${item.key}:${item.quantity}:${serializedProperties}`;
      })
      .join('|');

    return [
      serializedItems,
      this.context?.event_source || '',
      this.context?.event_code || '',
    ].join('::');
  }

  async getCheckoutUrl() {
    const cart = await this.fetchCart();
    const fingerprint = this.buildCartFingerprint(cart);

    if (this.checkoutCache && this.checkoutCache.fingerprint === fingerprint) {
      return {
        checkoutUrl: this.checkoutCache.checkoutUrl,
        warning: this.checkoutCache.warning || null,
      };
    }

    const input = this.buildCartInput(cart);

    let warning = null;
    let result = await this.executeCartCreate(input);

    if (!result.cart && input.discountCodes?.length > 0) {
      const fallbackInput = { ...input };
      delete fallbackInput.discountCodes;
      result = await this.executeCartCreate(fallbackInput);

      if (result.cart) {
        warning = 'Code salon indisponible sur ce panier, lien généré sans remise.';
      }
    }

    if (!result.cart?.checkoutUrl) {
      const firstError = result.userErrors?.[0]?.message || 'Erreur inconnue Storefront API';
      throw new Error(firstError);
    }

    const checkoutUrl = result.cart.checkoutUrl;

    this.checkoutCache = {
      fingerprint,
      checkoutUrl,
      warning,
    };

    window.dispatchEvent(
      new CustomEvent('kiosk:checkout-link-ready', {
        detail: {
          checkoutUrl,
        },
      })
    );

    return { checkoutUrl, warning };
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart API HTTP ${response.status}`);
    }

    return response.json();
  }

  buildCartInput(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const lines = items
      .map((item) => {
        const variantId = item?.variant_id;
        const quantity = parseInt(item?.quantity, 10);

        if (!variantId || Number.isNaN(quantity) || quantity <= 0) {
          return null;
        }

        const attributes = this.buildLineAttributes(item.properties);
        const line = {
          merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
          quantity,
        };

        if (attributes.length > 0) {
          line.attributes = attributes;
        }

        return line;
      })
      .filter(Boolean);

    if (lines.length === 0) {
      throw new Error('Le panier ne contient aucune ligne valide pour le checkout.');
    }

    const attributes = this.buildCartAttributes(items);

    const input = {
      lines,
    };

    if (attributes.length > 0) {
      input.attributes = attributes;
    }

    if (this.context?.event_code) {
      input.discountCodes = [this.context.event_code];
    }

    return input;
  }

  buildLineAttributes(properties) {
    if (!properties || typeof properties !== 'object') {
      return [];
    }

    return Object.entries(properties)
      .map(([key, value]) => {
        if (isBlank(key) || isBlank(value)) {
          return null;
        }

        return {
          key: String(key).slice(0, 255),
          value: String(value).slice(0, 255),
        };
      })
      .filter(Boolean);
  }

  buildCartAttributes(items) {
    const attributes = [];

    if (this.context?.event_source) {
      attributes.push({
        key: 'event_source',
        value: this.context.event_source,
      });
    }

    attributes.push({
      key: 'kiosk',
      value: '1',
    });

    const configurationIds = items
      .map((item) => item?.properties?.['_Configuration ID'])
      .filter((value) => !isBlank(value));

    if (configurationIds.length > 0) {
      attributes.push({
        key: 'cfg_ids',
        value: configurationIds.join(',').slice(0, 255),
      });
    }

    return attributes;
  }

  async executeCartCreate(input) {
    const response = await fetch(STOREFRONT_GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken,
      },
      body: JSON.stringify({
        query: CART_CREATE_MUTATION,
        variables: { input },
      }),
    });

    if (!response.ok) {
      const payload = await response.text().catch(() => '');
      throw new Error(`Storefront API HTTP ${response.status}: ${payload}`);
    }

    const payload = await response.json();

    if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
      const message = payload.errors[0]?.message || 'Erreur GraphQL';
      throw new Error(message);
    }

    const result = payload?.data?.cartCreate;

    if (!result) {
      throw new Error('Réponse cartCreate invalide.');
    }

    return {
      cart: result.cart || null,
      userErrors: Array.isArray(result.userErrors) ? result.userErrors : [],
    };
  }

}

function initializeKioskCartCheckout() {
  const manager = new KioskCartCheckout();
  manager.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeKioskCartCheckout, {
    once: true,
  });
} else {
  initializeKioskCartCheckout();
}
